import { Avatar, Drawer, Form, Space, Tabs, Typography, message } from "antd";
import { CloseOutlined } from "@ant-design/icons";
import React, { useContext, useEffect, useMemo, useState } from "react";
import { useLazyQuery } from "@apollo/client";
import { GET_SUPPORTED_LANGUAGES_BY_LOCATION } from "../../../graphql/query";
import { parseUserSettings } from "../../../utils/userHelpers";
import { isAllLocationSelected } from "../../../library/helpers";
import { getActiveSupportedLanguages } from "../../../utils/languages";
import useLang from "../../../hooks/useLang";
import {
  getLocalStorage,
  setLocalStorage,
  SKIP_ARCHIVE_RESTORE_CONFIRMATION_KEY,
} from "../../../utils/storages";
import { s3uploader } from "../../../utils/awsS3MediaUploader";
import { PHONE_USAGE_VALUES } from "../../commonComponents/PhoneNumbersField";
import CustomButton from "../../commonComponents/button";
import { UserContext } from "../../../amplify/authenticator-provider";
import ProfileTab from "./ProfileTab";
import CRMInfoTab from "./CRMInfoTab";
import SecurityTab from "./SecurityTab";
import NotificationsTab from "./NotificationsTab";
import ProfileDrawerContent from "./ProfileDrawerContent";
import CRMDrawerContent from "./CRMDrawerContent";
import { CRM_TYPES } from "../../../constants/crmConstants";

const { Title, Text } = Typography;

const PROFILE_PIC_S3_FOLDER = "/images/user-profiles";

const buildPhoneNumbers = user => {
  const parsedSettings = parseUserSettings(user?.settings);
  if (
    parsedSettings?.PHONE_NUMBERS &&
    Array.isArray(parsedSettings.PHONE_NUMBERS) &&
    parsedSettings.PHONE_NUMBERS.length > 0
  ) {
    return parsedSettings.PHONE_NUMBERS.map((phone, index) => ({
      id: Date.now() + index,
      type: phone.type || "Personal",
      number: phone.number || "",
      usedFor: phone.usedFor || ["All"],
      isPrimary: phone.isPrimary || index === 0,
    }));
  }
  if (user?.phone) {
    return [
      { id: Date.now(), type: "Personal", number: user.phone, usedFor: ["All"], isPrimary: true },
    ];
  }
  return [{ id: Date.now(), type: "Personal", number: "", usedFor: ["All"], isPrimary: true }];
};

const UserProfileForm = ({ user, onSave }) => {
  const [lang] = useLang();
  const { user: userContext } = useContext(UserContext);

  // Show CRM tab only when the current (non-"All") location has an active DS CRM integration
  const shouldShowCRMTab = useMemo(() => {
    if (isAllLocationSelected()) return false;

    const currentLocationId = sessionStorage.getItem("currentLocationId");
    const currentLocation = (userContext?.locations || []).find(
      loc => +loc?.location?.id === +currentLocationId
    );
    const { location } = currentLocation || {};
    return (
      location?.crmIntegration?.integrationType === CRM_TYPES.DEALERSOCKET &&
      location?.crmIntegration?.active
    );
  }, [userContext?.locations]);

  // States
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [drawerType, setDrawerType] = useState(""); // "profile" or "crm"
  const [languages, setLanguages] = useState([]);
  // Drawer-local profile-picture draft (never touches shared `user` until Save)
  const [profilePicFile, setProfilePicFile] = useState(null); // new file → uploaded to S3 on save
  const [profilePicPreview, setProfilePicPreview] = useState(""); // preview shown in the picker
  const [saving, setSaving] = useState(false); // drawer Save loader
  const [savingPassword, setSavingPassword] = useState(false); // Security Save loader
  const [skipArchiveRestoreConfirm, setSkipArchiveRestoreConfirm] = useState(
    () => getLocalStorage(SKIP_ARCHIVE_RESTORE_CONFIRMATION_KEY) === true
  );
  const [phoneNumbers, setPhoneNumbers] = useState(() => buildPhoneNumbers(user));

  // Form instances
  const [profileForm] = Form.useForm();
  const [crmForm] = Form.useForm();
  const [passwordForm] = Form.useForm();

  const settings = parseUserSettings(user?.settings);

  // Editable form data for the read-only Profile tab
  const [profileData, setProfileData] = useState({
    firstName: user?.firstName || "",
    lastName: user?.lastName || "",
    preferredName: settings?.preferredName || "",
    companyRole: user?.companyRole?.name || "",
    email: user?.email || "",
    phonePersonal: user?.phone || "",
    phoneWork: user?.phoneWork || "",
  });

  // Fetch supported languages lazily
  const [fetchSupportedLanguages, { loading: languagesLoading }] = useLazyQuery(
    GET_SUPPORTED_LANGUAGES_BY_LOCATION,
    {
      fetchPolicy: "network-only",
      onCompleted: res => setLanguages(getActiveSupportedLanguages(res?.getSupportedLanguages)),
    }
  );

  const loadSupportedLanguages = () => {
    if (languages.length > 0 || languagesLoading) return;
    fetchSupportedLanguages({
      variables: {
        locationId: isAllLocationSelected() ? null : sessionStorage.getItem("currentLocationId"),
      },
    });
  };

  // Load languages once on mount so the read-only view shows the label (not the code)
  useEffect(() => {
    loadSupportedLanguages();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Keep profileData in sync when the user prop changes
  useEffect(() => {
    const userSettings = parseUserSettings(user?.settings);
    setProfileData({
      firstName: user?.firstName || "",
      lastName: user?.lastName || "",
      preferredName: user?.preferredName ?? userSettings?.preferredName ?? "",
      companyRole: user?.companyRole?.name || "",
      email: user?.email || "",
      phonePersonal: user?.phone || "",
      phoneWork: user?.phoneWork || "",
    });
  }, [user]);

  const handleSkipArchiveRestoreConfirmToggle = checked => {
    setSkipArchiveRestoreConfirm(checked);
    setLocalStorage(SKIP_ARCHIVE_RESTORE_CONFIRMATION_KEY, checked);
  };

  // Phone number management
  const updatePhoneNumber = (phoneId, field, value) => {
    if (field === "usedFor") {
      if (!value || value.length === 0) return; // at least one usage required
      setPhoneNumbers(prev =>
        prev.map(phone => {
          if (phone.id !== phoneId) return phone;
          const hadAll = (phone.usedFor || []).includes("All");
          const hasAll = value.includes("All");
          let updatedValue = value;
          if (hasAll && !hadAll) {
            updatedValue = ["All"]; // "All" just selected → keep only "All"
          } else if (hadAll && value.length > 1) {
            updatedValue = value.filter(v => v !== "All"); // added an option → drop "All"
          }
          return { ...phone, usedFor: updatedValue };
        })
      );
      return;
    }
    setPhoneNumbers(prev =>
      prev.map(phone => (phone.id === phoneId ? { ...phone, [field]: value } : phone))
    );
  };

  const addPhoneNumber = () => {
    setPhoneNumbers(prev => [
      ...prev,
      { id: Date.now(), type: "Personal", number: "", usedFor: [], isPrimary: false },
    ]);
  };

  const removePhoneNumber = id => {
    if (phoneNumbers.length > 1) {
      setPhoneNumbers(prev => prev.filter(phone => phone.id !== id));
    }
  };

  // Profile picture preview list (from the drawer draft) + initials
  const profilePicList = profilePicPreview
    ? [{ uid: "-1", name: "profile.png", status: "done", url: profilePicPreview }]
    : [];
  const userInitials =
    `${user?.firstName?.[0] || ""}${user?.lastName?.[0] || ""}`.toUpperCase() || "U";

  // Validate + capture the selected file; only the draft preview changes (not `user`)
  const beforeProfilePicUpload = file => {
    const isLt5M = file.size / 1024 / 1024 < 5;
    if (!isLt5M) {
      message.error("Image must be smaller than 5MB!");
      return false;
    }
    setProfilePicFile(file);
    const reader = new FileReader();
    reader.onload = () => setProfilePicPreview(reader.result);
    reader.readAsDataURL(file);
    return false; // prevent antd auto upload — we upload on save
  };

  const handleProfilePicRemove = () => {
    setProfilePicFile(null);
    setProfilePicPreview("");
    return true;
  };

  // Drawer open/close
  const handleOpenDrawer = type => {
    setDrawerType(type);
    setDrawerVisible(true);

    if (type === "profile") {
      // Seed the drawer drafts from `user` (no shared-state mutation while editing)
      setPhoneNumbers(buildPhoneNumbers(user));
      setProfilePicFile(null);
      setProfilePicPreview(user?.profilePic || "");
      profileForm.setFieldsValue({
        firstName: profileData.firstName,
        lastName: profileData.lastName,
        preferredName: profileData.preferredName,
        email: profileData.email,
        preferredLanguage: user?.preferredLanguageCode,
        isTranslationEnabled: user?.isTranslationEnabled || false,
      });
    } else if (type === "crm") {
      const parsedSettings = parseUserSettings(user?.settings);
      const dsAgentSettings = parsedSettings?.DS_AGENT || {};
      const dsUserData = user?.dsUsers?.[0] || user?.dsUser || {};
      crmForm.setFieldsValue({
        active: dsUserData?.active || false,
        dsUserId: dsUserData?.dsUserId || "",
        dsUserName: dsUserData?.dsUserName || "",
        dmsId: dsAgentSettings?.DMS_ID || "",
        inboundId: dsAgentSettings?.INBOUND_ID || "",
        inboundId2: dsAgentSettings?.INBOUND_ID_2 || "",
        outboundId: dsAgentSettings?.OUTBOUND_ID || "",
      });
    }
  };

  // Drawer edits never touch shared `user` until Save, so closing just clears the drafts.
  const handleCloseDrawer = () => {
    setDrawerVisible(false);
    profileForm.resetFields();
    crmForm.resetFields();
    setProfilePicFile(null);
    setProfilePicPreview("");
  };

  // Save profile
  const handleSaveProfile = () => {
    profileForm
      .validateFields()
      .then(async values => {
        if (!phoneNumbers[0]?.number || phoneNumbers[0].number.trim() === "") {
          message.error("First phone number is required");
          return;
        }
        if (!phoneNumbers[0]?.usedFor || phoneNumbers[0].usedFor.length === 0) {
          message.error("Please select what the phone number is used for");
          return;
        }
        for (let i = 0; i < phoneNumbers.length; i++) {
          const phone = phoneNumbers[i];
          if (phone.number && phone.number.trim() !== "") {
            if (!phone.usedFor || phone.usedFor.length === 0) {
              message.error(`Please select what phone number ${i + 1} is used for`);
              return;
            }
          }
        }

        const validPhoneNumbers = phoneNumbers.filter(
          phone =>
            phone.number && phone.number.trim() !== "" && phone.usedFor && phone.usedFor.length > 0
        );
        if (validPhoneNumbers.length === 0) {
          message.error("At least one phone number with usage type is required");
          return;
        }

        const phoneNumbersForSave = validPhoneNumbers.map((phone, index) => ({
          number: phone.number,
          type: phone.type,
          // "All" is a UI shortcut — expand to actual usage values in the payload
          usedFor: phone.usedFor.includes("All") ? PHONE_USAGE_VALUES : phone.usedFor,
          isPrimary: index === 0,
        }));

        const existingSettings = parseUserSettings(user?.settings);
        const updatedSettings = {
          ...existingSettings,
          preferredName: values.preferredName || "",
          PHONE_NUMBERS: phoneNumbersForSave,
        };

        // Show the Save loader for the whole operation (photo upload + mutation)
        setSaving(true);
        try {
          // Resolve the profile picture from the drawer draft:
          //  - new file selected → upload to S3
          //  - otherwise use the draft preview (existing url, or "" if removed)
          let profilePic = profilePicPreview;
          if (profilePicFile) {
            const deployedUrl = await s3uploader(
              profilePicFile,
              `${process.env.GATSBY_S3_BUCKET}${PROFILE_PIC_S3_FOLDER}`,
              () => {},
              user?.id || "",
              null,
              null,
              null
            );
            profilePic = deployedUrl?.url;
          }

          const updatedUser = {
            ...user,
            firstName: values.firstName,
            lastName: values.lastName,
            preferredName: values.preferredName,
            email: values.email,
            phone: validPhoneNumbers[0]?.number || "",
            preferredLanguageCode: values.preferredLanguage,
            isTranslationEnabled: values.isTranslationEnabled || false,
            profilePic: profilePic || null, // removed image → send null, not ""
            settings: updatedSettings,
          };

          // Persist; the parent refetches ME and updates `user` (which syncs profileData)
          await onSave?.(updatedUser);
          handleCloseDrawer();
        } catch (err) {
          // Show upload errors; the parent shows mutation errors. Keep drawer open to retry.
          message.error({
            content: lang.somethingWentWrong || "Something went wrong",
            key: "profile-save",
          });
        } finally {
          setSaving(false);
        }
      })
      .catch(() => {
        message.error("Please check all required fields");
      });
  };

  // Save CRM
  const handleSaveCRM = () => {
    crmForm.validateFields().then(async values => {
      const existingSettings = parseUserSettings(user?.settings);
      const updatedSettings = {
        ...existingSettings,
        DS_AGENT: {
          DMS_ID: values.dmsId || null,
          INBOUND_ID: values.inboundId || null,
          OUTBOUND_ID: values.outboundId || null,
          INBOUND_ID_2: values.inboundId2 || null,
        },
      };
      const dsUserData = user?.dsUsers?.[0];
      const updatedUser = {
        ...user,
        dsUser: {
          id: dsUserData?.id || null,
          active: values.active || false,
          ...(values.dsUserId && { dsUserId: values.dsUserId }),
          ...(values.dsUserName && { dsUserName: values.dsUserName }),
        },
        settings: updatedSettings,
      };

      try {
        setSaving(true);
        // Persist; the parent refetches ME and updates `user` with the latest server data
        await onSave?.(updatedUser);
        handleCloseDrawer();
      } catch (err) {
        // Parent shows the error toast; keep the drawer open so the user can retry
      } finally {
        setSaving(false);
      }
    });
  };

  // Change password
  const handleChangePassword = async values => {
    const updatedUser = {
      ...user,
      oldPass: values.currentPassword,
      newPass: values.newPassword,
      newPassRep: values.confirmPassword,
    };
    try {
      setSavingPassword(true);
      // Persist; the parent refetches ME and updates `user` with the latest server data
      await onSave?.(updatedUser);
      passwordForm.resetFields();
    } catch (err) {
      // Parent shows the error toast; keep the values so the user can retry
    } finally {
      setSavingPassword(false);
    }
  };

  const tabItems = [
    {
      key: "1",
      label: "Profile",
      children: (
        <ProfileTab
          user={user}
          profileData={profileData}
          languages={languages}
          onEdit={() => handleOpenDrawer("profile")}
          skipArchiveRestoreConfirm={skipArchiveRestoreConfirm}
          onToggleSkipArchive={handleSkipArchiveRestoreConfirmToggle}
        />
      ),
    },
    ...(shouldShowCRMTab
      ? [
          {
            key: "2",
            label: "CRM Info",
            children: <CRMInfoTab user={user} onEdit={() => handleOpenDrawer("crm")} />,
          },
        ]
      : []),
    {
      key: "3",
      label: "Security",
      children: (
        <SecurityTab
          passwordForm={passwordForm}
          onFinish={handleChangePassword}
          saving={savingPassword}
        />
      ),
    },
    {
      key: "4",
      label: "Notifications",
      children: <NotificationsTab user={user} />,
    },
  ];

  return (
    <>
      {/* Header with Avatar and User Info (read-only display) */}
      <div className="flex items-center mb-6 mt-5 gap-4">
        <Avatar size={70} src={user?.profilePic || undefined}>
          {userInitials}
        </Avatar>
        <div>
          <Title level={4} className="!mt-0 !mb-1">
            {user?.firstName && user?.lastName
              ? `${user.firstName} ${user.lastName}`
              : user?.firstName || user?.lastName || "User"}
          </Title>
          <Text type="secondary">{user?.email || "No email"}</Text>
        </div>
      </div>

      {/* Tabs */}
      <Tabs defaultActiveKey="1" items={tabItems} />

      {/* Edit Drawer */}
      {drawerVisible && (
        <Drawer
          title="Edit profile"
          placement="right"
          onClose={handleCloseDrawer}
          open={drawerVisible}
          width="min(600px, 100vw)"
          closeIcon={<CloseOutlined />}
          footer={
            <div className="text-left">
              <Space>
                <CustomButton
                  type="primary"
                  handleClick={drawerType === "profile" ? handleSaveProfile : handleSaveCRM}
                  btnText="Save"
                  loading={saving}
                />
                <CustomButton handleClick={handleCloseDrawer} btnText="Cancel" disabled={saving} />
              </Space>
            </div>
          }
        >
          {drawerType === "profile" ? (
            <ProfileDrawerContent
              profileForm={profileForm}
              user={user}
              profilePicList={profilePicList}
              userInitials={userInitials}
              languages={languages}
              languagesLoading={languagesLoading}
              phoneNumbers={phoneNumbers}
              onUpdatePhone={updatePhoneNumber}
              onAddPhone={addPhoneNumber}
              onRemovePhone={removePhoneNumber}
              beforeProfilePicUpload={beforeProfilePicUpload}
              onProfilePicRemove={handleProfilePicRemove}
              onLoadLanguages={loadSupportedLanguages}
              skipArchiveRestoreConfirm={skipArchiveRestoreConfirm}
              onToggleSkipArchive={handleSkipArchiveRestoreConfirmToggle}
            />
          ) : (
            <CRMDrawerContent crmForm={crmForm} />
          )}
        </Drawer>
      )}
    </>
  );
};

export default UserProfileForm;
